/**
 * The Document detail's data hook. Loads the document (the real
 * `GET /v1/documents/{id}`), the tenant taxonomy (the change-category picker),
 * the location/scanner registries (to resolve capture ids to names, best-effort)
 * and the partner's document-field visibility (tasks/14 — which tabs/fields this
 * tenant's screen should hide), the last one also best-effort: a fetch failure
 * defaults to nothing hidden rather than risk hiding real data.
 * `reload()` re-fetches — the error-state Retry and the post-correction refresh.
 *
 * While the document itself is still mid-pipeline (`RECEIVED`, `OCR_DONE`, or
 * `PENDING_CONFIDENCE`), the hook re-loads itself on a timer — same reasoning and cadence as
 * `useDocuments`' list-level polling — so the status badge on this screen
 * actually advances (`OCR running` → `Classifying` → terminal) on its own,
 * not just on the next manual reload. Those refreshes are quiet: they swap
 * the document in place rather than dropping back to the loading skeleton.
 * The loop ends the moment the document reaches a terminal status.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../api/errors';
import type {
  DocumentFieldVisibility,
  DocumentView,
  EffectiveCategory,
  Location,
  Scanner,
} from '../../api/types';
import { useSession } from '../../auth/session';
import { usePolling } from '../../lib/hooks';

import { LIVE_POLL_MS } from './useDocuments';

/** Nothing hidden — the safe fallback when the visibility fetch fails, so a
 * partner's config never accidentally hides real data on a fetch hiccup. */
const DEFAULT_VISIBILITY: DocumentFieldVisibility = {
  hidden_tabs: [],
  hidden_overview_fields: [],
  hidden_metadata_fields: [],
};

export interface DocumentData {
  document: DocumentView;
  categories: EffectiveCategory[];
  locations: Location[];
  scanners: Scanner[];
  visibility: DocumentFieldVisibility;
}

export type DocumentState =
  | { status: 'loading' }
  | { status: 'not-found'; reload: () => void }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | { status: 'ready'; data: DocumentData; reload: () => void };

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const p = error.problem;
    const id = p.correlation_id ?? p.trace_id ?? p.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Still moving through the pipeline — the status badge has something to poll for. */
function isInFlight(document: DocumentView): boolean {
  return (
    document.status === 'RECEIVED' ||
    document.status === 'OCR_DONE' ||
    document.status === 'PENDING_CONFIDENCE'
  );
}

export function useDocument(documentId: string): DocumentState {
  const { api } = useSession();
  const [state, setState] = useState<DocumentState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  // Set only by the poll, and cleared by the load it belongs to: it decides
  // whether this run shows the loading skeleton or swaps the document in place.
  const quiet = useRef(false);

  const reload = useCallback(() => {
    quiet.current = false;
    setNonce((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    quiet.current = true;
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const silent = quiet.current;
    quiet.current = false;
    if (!silent) setState({ status: 'loading' });

    void (async () => {
      try {
        const [document, categoriesPage, locationsPage, scannersPage, visibility] = await Promise.all([
          api.documents.get(documentId, signal),
          api.categories.list(signal),
          api.settings.locations.list(signal).catch(() => null),
          api.settings.scanners.list(signal).catch(() => null),
          api.documentFields.mine(signal).catch(() => DEFAULT_VISIBILITY),
        ]);
        if (signal.aborted) return;
        setState({
          status: 'ready',
          reload,
          data: {
            document,
            categories: categoriesPage.items,
            locations: locationsPage?.items ?? [],
            scanners: scannersPage?.items ?? [],
            visibility,
          },
        });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        // A quiet poll that fails leaves the last good document on screen: a
        // few seconds stale beats replacing a working detail view with an
        // error page over one dropped poll.
        if (silent) return;
        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'not-found', reload });
          return;
        }
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [api, documentId, nonce, reload]);

  // `state` is the restart key: a new object per settled load re-arms the timer.
  usePolling(
    state.status === 'ready' && isInFlight(state.data.document),
    LIVE_POLL_MS,
    state,
    refresh,
  );

  return state;
}
